import React, {useContext, useEffect, useState} from "react";
import {Button, Stack} from "@mui/material";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@tanstack/react-query";
import dayjs from "dayjs";
import NewNote from "./NewNote";
import NoteList from "./NoteList";
import {IRaNote} from "../../models/models";
import {addRaNote, fetchRaNotes} from "../services/http.services";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";
import {NetworkScope} from "../../types";
import {hasScope} from "../../utils";

const RaNotes = () => {
    const {t} = useTranslation(['network']);
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [showNewNote, setShowNewNote] = useState<boolean>(false);
    const [notes, setNotes] = useState<IRaNote[]>([]);

    const {id} = useParams();

    const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const manageNotes = (data: IRaNote[]) => {
        const notes =
            data.map(note => {
                    note.requestCreationDate = dayjs(note.requestCreationDate).format('DD-MM-YYYY HH:mm');
                    return note;
                });
        setNotes(notes);
    }

    const fetchRaNotesQuery = useQuery(
        ['raNotes', {id: id}],
        () => fetchRaNotes({idRentalAgreement: id}),
        {
            enabled: false,
            onSuccess: (response: IRaNote[]) => {
                manageNotes(response);
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    const saveRaNoteMutation = useMutation(
        addRaNote,
        {
            onSuccess: async (response: any) => {
                manageNotes(response);
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater')
            }
        });


    const handleClickAddNote = (e: React.BaseSyntheticEvent) => {
        if (!!e) {
            e.stopPropagation();
        }

        setShowNewNote(true);
    }

    const handleCloseNewNote = () => {
        setShowNewNote(false);
    }

    const handleOnSave = (note: IRaNote | null) => {
        if (!!note) {
            const payload: any = {
                idRentalAgreement: id,
                body: note
            };
            saveRaNoteMutation.mutate(payload);
            handleCloseNewNote();
        }
    }

    useEffect(() => {
        fetchRaNotesQuery.refetch();
    }, []);
    return (
        <Stack rowGap={2}>
            <Stack direction="row" justifyContent="end">
                {
                    hasScope(globalData, NetworkScope.ManageRaNote) &&
                    <Button variant="outlined"
                            onClick={handleClickAddNote}
                            disabled={showNewNote} >
                        {t("raDetail.notes.button.addNote")}
                    </Button>
                }
            </Stack>

            <NewNote show={showNewNote} onClose={handleCloseNewNote} onSave={handleOnSave}/>

            <NoteList isShowingNewNote={showNewNote} notes={notes} />

            <CustomLoader loading={fetchRaNotesQuery.isFetching || saveRaNoteMutation.isLoading} />
        </Stack>
    )
}

export default RaNotes;